import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { fetchAbout } from "src/redux/slices/about";
import { localizedProps } from "src/utils/localize";

// СОДЕРЖИМОЕ БЛОКА «О НАС» — ОДНО НА ВЕСЬ САЙТ.
//
// Раньше главная страница брала этот блок только из админки: пустая настройка
// `about` — и с сайта исчезали и рассказ о компании, и все четыре цифры. На
// главной от «о нас» оставалась одна ссылка «Подробнее», а первый экран стоял
// без цифр. Страница /about в такой ситуации выглядела законченной — у неё
// запасные тексты в переводах были, а у главной нет.
//
// Хук убирает эту разницу: он отдаёт содержимое из админки, а чего в админке
// нет — берёт из переводов (ключи aboutPage.*, тексты из брифа клиента).
// Правило простое: заполнил владелец поле в админке — показываем его текст;
// не заполнил — показываем фирменный. Пустоты на сайте не бывает никогда.
//
// Возвращает готовые к выводу объекты { value, attrs }: attrs несёт lang=""
// когда сработал запасной язык (см. src/utils/localize.js).

const FALLBACK_ADVANTAGES = ["adv1", "adv2", "adv3", "adv4", "adv5"];
const FALLBACK_STATS = ["stat1", "stat2", "stat3", "stat4"];

const plain = (value) => ({ value, attrs: {} });

export default function useAboutContent() {
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const { data, status } = useSelector((state) => state.about);

  // Загрузку запускает сам хук: любая секция, которой нужен блок «О нас»,
  // получает данные фактом использования, и порядок секций на странице
  // перестаёт что-либо значить.
  useEffect(() => {
    if (status === "idle") dispatch(fetchAbout());
  }, [status, dispatch]);

  const adminTitle = localizedProps(data?.title, lang);
  const adminText = localizedProps(data?.text, lang);

  const advantages = data?.advantages?.length
    ? data.advantages.map((item) => ({
        title: localizedProps(item?.title, lang),
        text: localizedProps(item?.text, lang),
      }))
    : FALLBACK_ADVANTAGES.map((key) => ({
        title: plain(t(`aboutPage.${key}Title`)),
        text: plain(t(`aboutPage.${key}Text`)),
      }));

  const stats = data?.stats?.length
    ? data.stats.map((stat) => ({
        value: stat?.value,
        label: localizedProps(stat?.label, lang),
      }))
    : FALLBACK_STATS.map((key) => ({
        value: t(`aboutPage.${key}Value`),
        label: plain(t(`aboutPage.${key}Label`)),
      }));

  return {
    title: adminTitle.value ? adminTitle : plain(t("about.title")),
    // Запасной рассказ — два абзаца: кто мы и почему нам можно верить.
    // Из админки текст приходит одной строкой, абзацы в нём разделены
    // пустой строкой — поэтому и здесь тот же формат.
    text: adminText.value
      ? adminText
      : plain(`${t("aboutPage.lead1")}\n\n${t("aboutPage.lead2")}`),
    advantages,
    stats,
    statsTitle: t("aboutPage.statsTitle"),
    // Пришло ли содержимое из админки. Нужно тем местам, где важно отличать
    // «владелец так написал» от «показываем фирменный текст».
    fromAdmin: Boolean(adminText.value || data?.advantages?.length || data?.stats?.length),
  };
}
